import { ContextMenuHandler } from "@asup/context-menu";
import { defaultEditorDefns } from "__dummy__/TestEditor";
import React, { useMemo } from "react";
import styles from "../aic.module.css";
import { MosaicTrafficLightClass } from "../traffic-lights/MosaicTrafficLightClass";
import { classMerge } from "../utility/classMerge";
import { AitCellWindow } from "./AitCellWindow";
import { useTableContext } from "./TableContextProvider";
import { getBaseId } from "./functions/getBaseId";
import {
  AitHeaderCellData,
  AitLocationHeaderCell,
  AitLocationHeaderRow,
  AitTablePosition,
  SHOW_OPTIONS_WINDOW,
  UPDATE_HEADER_CELL,
} from "./functions/interface";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";

interface AitHeaderCellProps extends React.ComponentProps<"th"> {
  location: AitLocationHeaderCell;
}

/**
 * Handle click event on the cell to focus the internal editor.
 *
 * @param e - The event object
 */
export const clickCell: React.MouseEventHandler = (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.target instanceof HTMLTableCellElement) {
    const innerClick = new MouseEvent("click", { bubbles: true, cancelable: true });
    const editor = (e.target as HTMLTableCellElement).querySelector("div.aiev3");
    editor?.dispatchEvent(innerClick);
  }
};

/**
 * Render an editable header cell including editor and context window.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - The header cell location
 */
export const AitHeaderCell = <T,>({ location, ...rest }: AitHeaderCellProps) => {
  const { state, dispatch } = useTableContext<T>();
  const Editor = state?.editorProps.Editor;
  const thisCell: AitHeaderCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const thisHeaderRow = trGetPositionFromLocation(
    state,
    {
      ...location,
      position: AitTablePosition.headerRow,
    } as AitLocationHeaderRow,
    true,
  ).data;
  const id = getBaseId(location, state?.id);
  const displayText: T =
    thisCell?.replacedText ??
    thisCell?.text ??
    state?.editorProps.blankT ??
    (defaultEditorDefns.blankT as T);
  const rowHeaderColumns: number = state?.processedTableData?.rowHeaderColumns ?? 1;
  const decimalAlignPercent: number = state?.processedTableData?.decimalAlignPercent ?? 60;

  // Static options/variables
  const currentReadOnly = useMemo<boolean>(
    () => (!state || !thisCell ? true : !state.editable || location.isRepeat),
    [location.isRepeat, state, thisCell],
  );

  const displayColWidth = useMemo(() => {
    if ((thisCell?.repeatColSpan ?? thisCell?.colSpan ?? 1) >= 2) {
      /* istanbul ignore else */
      if (thisHeaderRow)
        return {
          width: thisHeaderRow.cells
            .slice(
              location.column,
              location.column + (thisCell!.repeatColSpan ?? thisCell!.colSpan),
            )
            .map(
              (c) =>
                c.colWidth ??
                // istanbul ignore next
                state!.defaultCellWidth,
            )
            .reduce((a, b) => a + b, 0),
          calculated: true,
        };
    }
    return { width: thisCell?.colWidth ?? 0 };
  }, [location.column, state, thisCell, thisHeaderRow]);

  // Update cell style when options change
  const cellStyle = useMemo<React.CSSProperties>(
    () =>
      !state || !thisCell
        ? {}
        : {
            overflow: "visible",
            width: `${state.colWidthMod * displayColWidth.width}px`,
            borderLeft: state.showCellBorders ? "1px dashed burlywood" : "",
            borderBottom: state.showCellBorders ? "1px dashed burlywood" : "",
            borderRight:
              state.showCellBorders &&
              location.column ===
                (state.processedTableData?.rowHeaderColumns ??
                  // istanbul ignore next
                  0) -
                  thisCell.colSpan
                ? "1px solid burlywood"
                : state.showCellBorders
                  ? "1px dashed burlywood"
                  : "",
            borderTop:
              state.showCellBorders &&
              location.row === 0 &&
              location.rowGroup !== undefined &&
              state.showCellBorders
                ? "1px dashed burlywood"
                : "",
          },
    [displayColWidth.width, location.column, location.row, location.rowGroup, state, thisCell],
  );

  // Traffic light
  const cellTrafficLight =
    !state?.noTrafficLights &&
    !location.isRepeat &&
    thisCell &&
    !["BlankNotEvaluated", "GreenOk"].includes(thisCell.coreTrafficLight)
      ? new MosaicTrafficLightClass(thisCell!.coreTrafficLight, "-4px", "-8px", "absolute").element
      : null;

  // Render element
  return !Editor ||
    !thisCell ||
    thisCell.colSpan === 0 ||
    thisCell.rowSpan === 0 ||
    thisCell.repeatColSpan === 0 ||
    thisCell.repeatRowSpan === 0 ||
    location.row === undefined ||
    location.column === undefined ? null : (
    <td
      {...rest}
      id={id}
      className={classMerge([
        styles.aitCell,
        styles.aitHeaderCell,
        currentReadOnly ? styles.aitReadonlyCell : "",
      ])}
      colSpan={thisCell.repeatColSpan ?? thisCell.colSpan}
      rowSpan={(thisCell.repeatRowSpan ?? thisCell.rowSpan) + (thisCell.spaceAfterSpan ?? 0)}
      style={cellStyle}
      onClick={clickCell}
    >
      <div
        style={{
          width: cellStyle.width,
          height: "100%",
        }}
      >
        {cellTrafficLight}
        <ContextMenuHandler
          className={styles.aitAieHolder}
          menuItems={[
            {
              label: "Show cell options",
              action: () => {
                const focusDummy = document.createElement("input");
                document.body.appendChild(focusDummy);
                focusDummy.focus();
                focusDummy.remove();
                dispatch({ operation: SHOW_OPTIONS_WINDOW, location });
              },
            },
          ]}
        >
          {/* Cell text editor */}
          <Editor
            id={`${id}-editor`}
            decimalAlignPercent={decimalAlignPercent}
            editable={!currentReadOnly}
            preReplacedText={thisCell.text}
            setValue={(ret) => {
              dispatch({ operation: UPDATE_HEADER_CELL, location, cellData: { text: ret } });
            }}
            className={styles.editorInput}
            textAlignment={
              !thisCell.justifyText || thisCell.justifyText === "default"
                ? location.column <
                  (rowHeaderColumns ??
                    // istanbul ignore next
                    0)
                  ? "left"
                  : "center"
                : thisCell.justifyText
            }
            value={displayText}
            commentConfig={{
              id:
                id ??
                // istanbul ignore next
                "",
              activeContext: "TlfItem",
              label: "New cell comment",
              iconPosition: { top: "-2px", left: "-2px" },
              noIcon: location.isRepeat,
            }}
          />
        </ContextMenuHandler>
      </div>
      <AitCellWindow location={location} />
    </td>
  );
};

AitHeaderCell.displayName = "AitHeaderCell";
